<template>
  <div class="workflow_container">
    <BaseStepper v-if="workflow" key="2" v-model="activeStepperStatus">
      <div
        v-if="completionRate.display"
        class="completion_rate_container custom_tooltip"
      >
        <div class="completion_rate_background">
          <div
            class="completion_rate_value"
            :style="{ width: `${completionRate.value}%` }"
          />
        </div>
        <div class="tooltip_content" v-text="completionRate.label" />
      </div>
      <BaseStepperHeader
        :no-border-top="completionRate.display"
        :title="$tc('utils.status', 2)"
      >
        <BaseStepperStep
          v-for="(status, index) in workflow.statuses"
          :key="`ss${status.id}-${index}`"
          :step="index + 1"
          :label="status.name"
          :complete="isComplete(status)"
          :active="folderActiveStatusId == status.id"
        />
      </BaseStepperHeader>

      <BaseStepperItems>
        <BaseStepperContent
          v-for="(status, index) in workflow.statuses"
          :key="`sc${index}${status.id}`"
          :step="index + 1"
        >
          <div class="stepper_content">
            <div class="status_section">
              <FolderWorkflowStatus
                :status="status"
                :loading="fetchingStatusDetail"
              />
            </div>
            <div class="action_section">
              <FolderDetailActions
                :status="status"
                :loading="fetchingStatusDetail"
                :automation-effects="status.effects"
              />
            </div>
          </div>
        </BaseStepperContent>
      </BaseStepperItems>
    </BaseStepper>

    <!-- DIALOG -->
    <FolderChangeStatusDialog />
    <FolderAskInformationDialog />
    <FolderOfferCreateAndEditDialog />
    <FolderStatusQuestionCreateDialog />
    <FolderWorkflowSendCosmoFormDialog v-if="isCosmoformDialogOpen" />
    <FolderWorkflowAnwserQuestionsDialog
      @questionAnswered="$emit('questionAnswered')"
    />
    <FolderGenerateDocxDialog
      v-if="isModalGenerateDocOpen"
      :is-open="isModalGenerateDocOpen"
      :active-folder="activeFolder"
      :status="activeStatus"
    />
  </div>
</template>

<script>
import FolderDetailActions from "@/modules/Folder/Components/FolderDetailActions";
import FolderWorkflowStatus from "@/modules/Folder/Components/FolderWorkflowStatus";
import FolderChangeStatusDialog from "@/modules/Folder/Components/FolderChangeStatusDialog";
import FolderAskInformationDialog from "@/modules/Folder/Components/FolderAskInformationDialog";
import FolderOfferCreateAndEditDialog from "@/modules/Folder/Components/FolderOfferCreateAndEditDialog";
import FolderStatusQuestionCreateDialog from "@/modules/Folder/Components/FolderStatusQuestionCreateDialog";
import FolderWorkflowSendCosmoFormDialog from "@/modules/Folder/Components/FolderWorkflowSendCosmoFormDialog";
import FolderWorkflowAnwserQuestionsDialog from "@/modules/Folder/Components/FolderWorkflowAnwserQuestionsDialog";
import { mapState } from "vuex";
import FolderGenerateDocxDialog from "./FolderGenerateDocxDialog.vue";

export default {
  name: "FolderWokflowDetail",
  components: {
    FolderDetailActions,
    FolderWorkflowStatus,
    FolderChangeStatusDialog,
    FolderAskInformationDialog,
    FolderOfferCreateAndEditDialog,
    FolderStatusQuestionCreateDialog,
    FolderWorkflowSendCosmoFormDialog,
    FolderWorkflowAnwserQuestionsDialog,
    FolderGenerateDocxDialog,
  },
  props: {
    folderActiveStatusId: {
      type: Number,
      required: true,
    },
    workflow: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      fetchingStatusDetail: false,
      fetchingAutomationEffect: true,
      activeStepperStatus: 0,
    };
  },
  computed: {
    ...mapState({
      activeWorkflow: (state) => state.workflow.activeWorkflow,
      isModalGenerateDocOpen: (state) =>
        state.folder.modals.isModalGenerateDocOpen,
      activeFolder: (state) => state.folder.activeFolder,
      isCosmoformDialogOpen: (state) => state.folder.modals.isSendCosmoFormOpen,
    }),
    activeStatus() {
      return this.activeWorkflow?.statuses?.find(
        (status) => status.id === this.folderActiveStatusId
      );
    },
    workflowId() {
      return this.workflow ? this.workflow.id : null;
    },
    completionRate() {
      const value = this.activeFolder?.completionRate
        ? Math.round(this.activeFolder.completionRate * 100) / 100
        : 0;
      return {
        display: !isNaN(parseInt(this.activeFolder?.completionRate)),
        value,
        label: `${this.$t("utils.completion_rate")} ${value} %`,
      };
    },
  },
  watch: {
    folderActiveStatusId(newVal, oldVal) {
      if (!isNaN(newVal) && !isNaN(oldVal)) this.updateActiveStepperStatus();
    },
    activeStepperStatus: {
      handler(newVal) {
        if (newVal) {
          this.getStatusDetail(parseInt(this.workflow.statuses[newVal - 1].id));
          this.$store.commit("folder/SET_ACTIVE_FOLDER", {
            ...this.activeFolder,
            activeStatus: +this.workflow.statuses[newVal - 1].id,
          });
        }
      },
      immediate: true,
    },
  },
  created() {
    this.updateActiveStepperStatus();
  },
  methods: {
    isComplete(status) {
      return this.activeStatus?.order > status?.order;
    },
    async updateActiveStepperStatus() {
      if (this.workflow) {
        const activeIndex =
          this.workflow.statuses.findIndex(
            (s) => s.id == this.folderActiveStatusId
          ) + 1;
        if (activeIndex) {
          this.activeStepperStatus = activeIndex;
        } else {
          this.activeStepperStatus =
            this.workflow.statuses.findIndex(
              (s) => s.id == this.workflow.primaryStatus
            ) + 1;
        }
      }
    },

    async getStatusDetail(statusId) {
      this.fetchingStatusDetail = true;
      try {
        if (this.$route.params.folderId) {
          await Promise.all([
            this.$store.dispatch("workflow/fetchFolderQuestions", {
              organizationId: this.$route.params.organizationId,
              workflowId: this.workflowId,
              folderId: this.$route.params.folderId,
              statusId: statusId,
            }),
            this.$store.dispatch("workflow/fetchFolderStatusActions", {
              organizationId: this.$route.params.organizationId,
              folderId: this.$route.params.folderId,
              statusId: statusId,
            }),
            this.$store.dispatch("workflow/fetchFolderStatusEffects", {
              organizationId: this.$route.params.organizationId,
              folderId: this.$route.params.folderId,
              statusId: statusId,
            }),
          ]);
        }
      } catch (error) {
        const { data, status } = error.response;
        if (status === 404 || status === 500) throw new Error(data.detail);
      }
      this.fetchingStatusDetail = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.workflow_container {
  margin-bottom: 24px;
  .completion_rate_container {
    cursor: pointer;
    height: 15px;
    margin-bottom: -5px;
    width: 100%;
    .completion_rate_background {
      background: #d3ecfd;
      height: 100%;
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
      overflow: hidden;
      .completion_rate_value {
        height: 100%;
        background: #8ad5fa;
      }
    }
  }
  .custom_tooltip {
    position: relative;
    .tooltip_content {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate(-50%, calc(-100% - 20px));
      padding: 8px 16px;
      border-radius: 5px;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.25s;
      background-color: #707080;
      z-index: 10;
      font-size: 11px;
      line-height: 14px;
      color: #fff;
      text-align: start;
    }
    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate(-50%, -21px);
      height: 0;
      width: 0;
      border: 12px solid transparent;
      border-top-color: #707080;
      z-index: 11;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.25s;
    }

    &:hover {
      .tooltip_content {
        opacity: 1;
      }
      &::before {
        opacity: 1;
      }
    }
  }
  .stepper_content {
    display: flex;
    .status_section {
      width: calc(100% - 350px);
      flex: none;
    }
    .action_section {
      width: 350px;
      flex: none;
    }
  }
}
</style>
